/**
 * «Тренды»: тематики Wordstat и подборки сотрудников. Вопрос экрана — «что
 * сейчас ищут и по чему у нас уже собраны референсы».
 *
 * Тематика — затравка Wordstat («свадебное платье»), внутри неё типы
 * («кружевное», «рыбка») с историей за 24 месяца. Подборка — та же тематика,
 * но своя у сотрудника: его папка слов, и только он решает, что из неё уходит
 * на сбор.
 *
 * Всё — одной таблицей с фильтром «мои / общие / коллег»: записей десятки, три
 * таблицы стопкой давали три шапки и прыгали, когда появлялась первая чужая
 * подборка. Свои — первыми. Создание — кнопкой «+» в окне: это редкое
 * действие, постоянная форма посреди списка платилась каждым визитом.
 *
 * Задачи сбора внизу — только идущие и упавшие: человеку важно «идёт или
 * сломалось», завершённые раскрываются кнопкой.
 */
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { Dialog } from "@/components/trends/Dialog";
import { NewTopicForm } from "@/components/trends/NewTopicForm";
import { TaskList } from "@/components/trends/TaskList";
import { btnGhost, btnPrimary, Chip, Empty, ErrorLine, Loading, Panel } from "@/components/trends/ui";
import { useIdentity } from "@/lib/identity";
import { num, when } from "@/lib/media";
import { isTaskActive, loadTopics, pollWhileActive, retryQuery, trendKeys, type TopicRow } from "@/lib/trends";

type Who = "all" | "mine" | "shared" | "others";

export function TrendsPage() {
  const me = useIdentity().data?.id;
  const [creating, setCreating] = useState(false);
  const [who, setWho] = useState<Who>("all");
  const [showDone, setShowDone] = useState(false);
  const list = useQuery({
    queryKey: trendKeys.list,
    queryFn: loadTopics,
    retry: retryQuery,
    refetchInterval: (q) => pollWhileActive(q.state.data?.tasks),
  });

  const topicNames = useMemo(
    () => new Map((list.data?.topics ?? []).map((t) => [t.id, t.name] as const)),
    [list.data],
  );

  const whoOf = (t: TopicRow): Exclude<Who, "all"> =>
    t.owner_user_id === 0 ? "shared" : t.owner_user_id === me ? "mine" : "others";

  const createButton = (
    <button type="button" className={btnPrimary} onClick={() => setCreating(true)}>
      <Plus size={14} />
      Новая подборка
    </button>
  );

  let body: ReactNode;
  if (list.isPending) body = <Loading />;
  else if (list.isError) body = <ErrorLine error={list.error} />;
  else {
    const { topics, tasks } = list.data;
    const order: Record<Exclude<Who, "all">, number> = { mine: 0, shared: 1, others: 2 };
    // Сортировка стабильна: внутри группы — порядок сервиса (свежие правки сверху).
    const sorted = [...topics].sort((a, b) => order[whoOf(a)] - order[whoOf(b)]);
    const count = (w: Who) => (w === "all" ? topics.length : topics.filter((t) => whoOf(t) === w).length);
    const rows = who === "all" ? sorted : sorted.filter((t) => whoOf(t) === who);
    const live = tasks.filter((t) => isTaskActive(t) || t.status === "failed");
    const running = tasks.filter(isTaskActive).length;
    const failed = tasks.filter((t) => t.status === "failed").length;
    const done = tasks.length - live.length;
    const shownTasks = showDone ? tasks : live;

    body = (
      <>
        <Panel annot="тематики и подборки">
          <div className="flex flex-wrap items-center gap-1.5 border-b border-border px-3 py-2">
            {(
              [
                ["all", "все"],
                ["mine", "мои"],
                ["shared", "общие"],
                ["others", "коллег"],
              ] as const
            ).map(([w, text]) => (
              <Chip key={w} on={who === w} onClick={() => setWho(w)}>
                {text} <span className="tnum text-fg-faint">{count(w)}</span>
              </Chip>
            ))}
          </div>
          {rows.length ? (
            <TopicsTable topics={rows} whoOf={whoOf} />
          ) : (
            <Empty>
              <div className="grid justify-items-center gap-2">
                {who === "mine" || topics.length === 0
                  ? "Своих подборок пока нет."
                  : who === "shared"
                    ? "Общих тематик пока нет."
                    : "Подборок коллег пока нет."}
                {who === "mine" || topics.length === 0 ? createButton : null}
              </div>
            </Empty>
          )}
        </Panel>

        {tasks.length ? (
          <Panel
            title={
              <>
                Задачи сбора
                {running ? <span className="ml-2 font-normal text-warn">идут {running}</span> : null}
                {failed ? <span className="ml-2 font-normal text-err">ошибок {failed}</span> : null}
              </>
            }
            aside={
              done ? (
                <button type="button" className={btnGhost} onClick={() => setShowDone((v) => !v)}>
                  {showDone ? "скрыть завершённые" : `ещё ${done} завершённых`}
                </button>
              ) : null
            }
            annot="задачи сбора"
          >
            {shownTasks.length ? (
              <TaskList tasks={shownTasks} topicNames={topicNames} />
            ) : (
              <Empty>Сейчас ничего не собирается.</Empty>
            )}
          </Panel>
        ) : null}
      </>
    );
  }

  return (
    <div className="grid gap-4 p-5">
      <header className="flex flex-wrap items-start gap-4">
        <div className="min-w-0">
          <h1 className="text-[18px] font-semibold tracking-[-0.01em] text-fg">Тренды</h1>
          <p className="mt-0.5 text-[12.5px] text-fg-muted">
            Что ищут и как меняется спрос. Подборка — ваша папка слов: кладёте слова и сами решаете, какое собирать
            с Pinterest. Тематики — общие, из затравки Wordstat.
          </p>
        </div>
        <div className="ml-auto">{createButton}</div>
      </header>

      {body}

      <Dialog open={creating} title="Новая подборка" onClose={() => setCreating(false)}>
        <NewTopicForm onCancel={() => setCreating(false)} />
      </Dialog>
    </div>
  );
}

const WHO_TEXT = { mine: "моя", shared: "общая" } as const;

function TopicsTable({ topics, whoOf }: { topics: TopicRow[]; whoOf: (t: TopicRow) => Exclude<Who, "all"> }) {
  const navigate = useNavigate();
  const th = (text: ReactNode, align: "left" | "right" = "right", title?: string) => (
    <th className={`border-b border-border px-3 py-2 ${align === "left" ? "text-left" : "text-right"}`} title={title}>
      {text}
    </th>
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
            {th("название", "left")}
            {th("чья", "left")}
            {th("затравка", "left")}
            {th("частота", "right", "Частота затравки за период таблицы Wordstat")}
            {th("слов", "right", "Слова подборки или типы тематики — то, что уходит в Pinterest")}
            {th("фраз", "right", "Фраз Wordstat по затравке")}
            {th("референсов", "right", "Скачанных референсов Pinterest по всем словам")}
            {th("собирали")}
          </tr>
        </thead>
        <tbody>
          {topics.map((t) => {
            const w = whoOf(t);
            return (
              <tr key={t.id} className="cursor-pointer hover:bg-bg-hover" onClick={() => navigate(`/trends/${t.id}`)}>
                <td className="border-b border-border px-3 py-0">
                  <Link
                    to={`/trends/${t.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="block py-2 font-medium text-fg hover:text-accent-text"
                  >
                    {t.name}
                  </Link>
                </td>
                <td className="border-b border-border px-3 py-2 whitespace-nowrap">
                  {w === "others" ? (
                    <span className="text-fg-muted">{t.owner_name ?? "коллега"}</span>
                  ) : (
                    <span
                      className={`rounded-[4px] px-1.5 text-[11.5px] ${
                        w === "mine" ? "bg-accent-dim text-accent-text" : "bg-neutral-bg text-fg-muted"
                      }`}
                    >
                      {WHO_TEXT[w]}
                    </span>
                  )}
                </td>
                <td className="border-b border-border px-3 py-2 text-fg-muted">
                  {t.phrase ?? <span className="text-fg-faint">—</span>}
                </td>
                <td className="border-b border-border px-3 py-2 text-right tnum">{num(t.total)}</td>
                <td className="border-b border-border px-3 py-2 text-right tnum">{num(t.types)}</td>
                <td className="border-b border-border px-3 py-2 text-right tnum text-fg-muted">{num(t.phrases)}</td>
                <td className="border-b border-border px-3 py-2 text-right tnum">
                  {t.refs > 0 ? (
                    <Link
                      to={`/trends/${t.id}/refs`}
                      onClick={(e) => e.stopPropagation()}
                      className="hover:text-accent-text hover:underline"
                      title="Все референсы подборки"
                    >
                      {num(t.refs)}
                    </Link>
                  ) : (
                    num(t.refs)
                  )}
                </td>
                <td className="border-b border-border px-3 py-2 text-right tnum text-fg-muted">
                  {t.date_collect ? when(t.date_collect) : <span className="text-fg-faint">ещё нет</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
